import React, { useState, useRef, useEffect } from 'react';
import Icon from '@mdi/react';
import { mdiClose, mdiMenuDown, mdiChevronUp, mdiCheck, mdiLoading, mdiSend, mdiRobot, mdiAccountCircle, mdiTrashCanOutline, mdiAlert } from '@mdi/js';
import { useStore } from '../store/store';
import { TextField, Button, Flex, Dialog, Text } from "@radix-ui/themes";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw';
import api from '../services/api';

export const SidebarChatContent = ({ chat, setSelectedChat }) => {
    const [state, actions] = useStore();
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [hoveredChapter, setHoveredChapter] = useState(null);
    const contextMenuRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [menuPositions, setMenuPositions] = useState({
        fullChapter: false,
        chapterSummary: false,
        scenes: false,
        chapters: {}
    });
    
    // Message input state
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    
    // Track current chat ID
    const [currentChatId, setCurrentChatId] = useState(chat?.id || null);

    // Selected context tracking
    const [selectedContext, setSelectedContext] = useState({
        fullBook: false,
        bookSummary: false,
        chapters: {},
        chapterSummaries: {},
        scenes: {},
        worldInfo: false // Add worldInfo flag
    });
    
    // Track current chat messages locally
    const [chatMessages, setChatMessages] = useState(chat?.messages || []);
    
    // Auto-scroll to the latest message
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
        }
    }, [chatMessages]);
    
    // Handle clicks outside the context menu to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
                setContextMenuOpen(false);
                setHoveredItem(null);
                setHoveredChapter(null);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Update currentChatId when chat changes
    useEffect(() => {
        if (chat?.id) {
            setCurrentChatId(chat.id);
        }
    }, [chat]);
    
    // Update local messages when chat prop changes
    useEffect(() => {
        if (chat?.messages) {
            setChatMessages(chat.messages);
        }
    }, [chat]);
    
    const toggleContextMenu = () => {
        setContextMenuOpen(!contextMenuOpen);
        if (contextMenuOpen) {
            setHoveredItem(null);
            setHoveredChapter(null);
        }
    };
    
    // Handle sending a message
    const handleSend = async () => {
        if (message.trim() && state.currentNovel?.id) {
            // Get the novel ID from the store
            const novelId = state.currentNovel.id;
            
            // Create user message
            const userMessage = {
                role: 'user',
                message: message,
                timestamp: new Date().toISOString(),
            };
            
            // Update local messages state immediately so the message appears
            setChatMessages(prevMessages => [...prevMessages, userMessage]);
            
            // If we have a chat update action, use it
            if (actions.addMessageToChat) {
                actions.addMessageToChat(chat.id, userMessage);
            }
            
            // Clear input and set loading state
            setMessage('');
            setSending(true);
            
            // Prepare world info data if selected
            let contextData = { ...selectedContext };
            if (selectedContext.worldInfo && state.currentNovel) {
                contextData.worldInfoData = {
                    locations: state.currentNovel.locations || [],
                    lore: state.currentNovel.lore || [],
                    characters: state.currentNovel.characters || []
                };
            }

            const model = JSON.parse(state.user.settings).models.chat;

            try {
                // Call the API with the current novel ID, selected context, message, and chat ID
                const response = await api.getChatResponse(
                    novelId, 
                    contextData, // Pass the enhanced context data with world info
                    message, 
                    model,
                    currentChatId
                );
                
                if (response && response.success && response.response) {
                    // Format the AI response
                    const aiMessage = {
                        role: 'assistant',
                        message: response.response,
                        timestamp: new Date().toISOString(),
                        cost: response.cost || 0
                    };
                    
                    // Update local messages state with AI response
                    setChatMessages(prevMessages => [...prevMessages, aiMessage]);
                    
                    // Add AI response to chat through the actions if available
                    if (actions.addMessageToChat) {
                        actions.addMessageToChat(chat.id, aiMessage);
                    }
                    
                    // Update current chat ID if this is a new chat
                    if (response.chatId && !currentChatId) {
                        setCurrentChatId(response.chatId);
                        
                        // Refresh chats list if we have that action
                        if (actions.refreshChats) {
                            actions.refreshChats();
                        }
                    }
                } else {
                    // Handle error response
                    const errorMessage = {
                        role: 'system',
                        message: 'Sorry, I had trouble generating a response. Please try again.',
                        timestamp: new Date().toISOString()
                    };
                    
                    // Update local messages with error
                    setChatMessages(prevMessages => [...prevMessages, errorMessage]);
                    
                    // Add error message to chat through actions if available
                    if (actions.addMessageToChat) {
                        actions.addMessageToChat(chat.id, errorMessage);
                    }
                }
            } catch (err) {
                console.error('Error sending message:', err);
                // Add error message to chat
                const errorMessage = {
                    role: 'system',
                    message: 'An error occurred. Please try again later.',
                    timestamp: new Date().toISOString()
                };
                
                // Update local messages with error
                setChatMessages(prevMessages => [...prevMessages, errorMessage]);
                
                // Add error message through actions if available
                if (actions.addMessageToChat) {
                    actions.addMessageToChat(chat.id, errorMessage);
                }
            } finally {
                setSending(false);
            }
        }
    };
    
    // Handle key press for sending
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };
    
    // Handle toggling context options
    const toggleContextOption = (type, id = null) => {
        setSelectedContext(prev => {
            const newState = { ...prev };
            
            if (type === 'fullBook') {
                newState.fullBook = !prev.fullBook;
            } else if (type === 'bookSummary') {
                newState.bookSummary = !prev.bookSummary;
            } else if (type === 'worldInfo') {
                newState.worldInfo = !prev.worldInfo; // Handle world info toggle
            } else if (type === 'chapter') {
                newState.chapters = { ...prev.chapters };
                newState.chapters[id] = !prev.chapters[id];
            } else if (type === 'chapterSummary') {
                newState.chapterSummaries = { ...prev.chapterSummaries };
                newState.chapterSummaries[id] = !prev.chapterSummaries[id];
            } else if (type === 'scene') {
                newState.scenes = { ...prev.scenes };
                newState.scenes[id] = !prev.scenes[id];
            }
            
            return newState;
        });
    };
    
    // Function to check if submenu should grow up
    const checkMenuPosition = (itemKey, event, chapterId = null) => {
        if (!event || !event.currentTarget) return;
        
        const targetRect = event.currentTarget.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const bottomSpace = windowHeight - targetRect.bottom;
        // If there's not enough room below (less than 250px), grow upward
        const shouldGrowUp = bottomSpace < 250;
        
        if (chapterId !== null) {
            // For chapter-specific submenus
            setMenuPositions(prev => ({
                ...prev,
                chapters: {
                    ...prev.chapters,
                    [chapterId]: shouldGrowUp
                }
            }));
        } else {
            // For main submenu items
            setMenuPositions(prev => ({
                ...prev,
                [itemKey]: shouldGrowUp
            }));
        }
    };
    
    // Handle chapter hover
    const handleChapterHover = (chapterId, e) => {
        setHoveredChapter(chapterId);
        checkMenuPosition('chapters', e, chapterId);
    };
    
    // Get display text for selected context
    const getSelectedContextDisplay = () => {
        const selected = [];
        
        if (selectedContext.fullBook) selected.push("Full Book");
        if (selectedContext.bookSummary) selected.push("Book Summary");
        if (selectedContext.worldInfo) selected.push("World Info"); // Add World Info to display
        
        const selectedChapters = Object.entries(selectedContext.chapters)
            .filter(([_, isSelected]) => isSelected)
            .length;
        if (selectedChapters > 0) selected.push(`${selectedChapters} Chapters`);
        
        const selectedChapterSummaries = Object.entries(selectedContext.chapterSummaries)
            .filter(([_, isSelected]) => isSelected)
            .length;
        if (selectedChapterSummaries > 0) selected.push(`${selectedChapterSummaries} Chapter Summaries`);
        
        const selectedScenes = Object.entries(selectedContext.scenes)
            .filter(([_, isSelected]) => isSelected)
            .length;
        if (selectedScenes > 0) selected.push(`${selectedScenes} Scenes`);
        
        return selected.length > 0 ? selected.join(', ') : 'No context selected';
    };

    // Add state for modal dialogs
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [deleteIndex, setDeleteIndex] = useState(null);

    // Handle deleting a message and all subsequent messages
    const handleDeleteMessage = async (index) => {
        if (!currentChatId) return;
        
        // Store the index and open the confirmation modal
        setDeleteIndex(index);
        setDeleteModalOpen(true);
    };
    
    // Execute the actual deletion after confirmation
    const confirmDelete = async () => {
        if (deleteIndex === null || !currentChatId) return;
        
        // Close the modal
        setDeleteModalOpen(false);
        
        // Get only messages up to the selected index
        const newMessages = chatMessages.slice(0, deleteIndex);
        
        // Update local state immediately
        setChatMessages(newMessages);
        
        try {
            // Update the chat in the database
            await api.updateChatMessages(currentChatId, newMessages);
            
            // Find the specific chat in currentNovel.chats and update it
            if (state.currentNovel && state.currentNovel.chats) {
                // Find the current chat in the novel's chats array
                const currentChat = state.currentNovel.chats.find(c => c.id === currentChatId);
                
                if (currentChat) {
                    // Create a new chat object with updated messages
                    const updatedChat = {
                        ...currentChat,
                        messages: newMessages
                    };
                    
                    // Create a new chats array with the updated chat
                    const updatedChats = state.currentNovel.chats.map(c => 
                        c.id === currentChatId ? updatedChat : c
                    );
                    
                    // Update the state with the new chats array
                    actions.setCurrentNovel({
                        ...state.currentNovel,
                        chats: updatedChats
                    });
                }
            }
        } catch (error) {
            console.error('Error deleting messages:', error);
            // Revert to original messages if there was an error
            setChatMessages(chatMessages);
            
            // Show error modal instead of alert
            setErrorMessage('Failed to delete messages. Please try again.');
            setErrorModalOpen(true);
        } finally {
            // Reset the delete index
            setDeleteIndex(null);
        }
    };

    return (
        <div>
            <div className='sidebar-sidebar sidebar-sidebar-chat'>
                <div style={{textAlign: 'right', cursor: 'pointer'}} onClick={() => setSelectedChat(null)}>
                    <Icon path={mdiClose} color={'#FFFFFF50'} size={1} />
                </div>
                <div className="sidebar-chat-messages">
                    {chatMessages.map((msg, index) => (
                        <div key={index} className={`sidebar-message ${msg.role}`}>
                            <div className="sidebar-message-delete-button" 
                                 onClick={() => handleDeleteMessage(index)}>
                                <Icon path={mdiClose} size={0.7} />
                            </div>
                            <div className="sidebar-message-icon">
                                {msg.role === 'assistant' ? 
                                    <Icon path={mdiRobot} size={1} /> : 
                                    msg.role === 'user' ? 
                                    <Icon path={mdiAccountCircle} size={1} /> : 
                                    <Icon path={mdiMenuDown} size={1} />
                                }
                            </div>
                            <div className="sidebar-message-content">
                                {msg.role === 'assistant' ? (
                                    <div className="sidebar-markdown-content">
                                        <ReactMarkdown 
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeRaw]}
                                            remarkRehypeOptions={{ passThrough: ['link'] }}
                                        >{msg.message}</ReactMarkdown>
                                    </div>
                                ) : (
                                    msg.message
                                )}
                                {msg.cost > 0 && (
                                    <div className="sidebar-message-cost">
                                        Cost: ${msg.cost.toFixed(6)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {sending && (
                        <div className="sidebar-message assistant sidebar-loading-message">
                            <div className="sidebar-message-icon">
                                <Icon path={mdiRobot} size={1} />
                            </div>
                            <div className="sidebar-message-content">
                                <div className="sidebar-typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {!state.readOnly && <div className="sidebar-chat-input">
                    <TextField.Root 
                        className="sidebar-chat-input-field" 
                        placeholder={sending ? "AI is thinking..." : "Ask a question about your story..."}
                        value={message} 
                        variant="soft"
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sending}
                    />
                    <Button 
                        className={`sidebar-chat-send-button ${sending ? 'sending' : ''}`}
                        onClick={handleSend}
                        disabled={sending || !message.trim() || !state.currentNovel?.id}
                    >
                        <Icon path={sending ? mdiLoading : mdiSend} size={1} spin={sending} />
                    </Button>
                </div>}
                {!state.readOnly && <div className='sidebar-chat-context-wrapper'>
                    <Flex justify={"between"} className="sidebar-chat-context-area">
                        <div className="sidebar-context-menu-container" ref={contextMenuRef}>
                            <button 
                                className={`sidebar-context-button ${contextMenuOpen ? 'active' : ''}`} 
                                onClick={toggleContextMenu}
                                disabled={!state.currentNovel?.chapters || state.currentNovel.chapters.length === 0}
                            >
                                Context <Icon path={mdiMenuDown} size={0.8} />
                            </button>
                            
                            {contextMenuOpen && (
                                <div className="sidebar-context-menu" style={{
                                    bottom: '100%',
                                    top: 'auto', 
                                    marginBottom: '5px',
                                    marginTop: 0
                                }}>
                                    <div 
                                        className={`sidebar-context-menu-item ${selectedContext.fullBook ? 'selected' : ''}`}
                                        onClick={() => toggleContextOption('fullBook')}
                                    >
                                        {selectedContext.fullBook && <Icon path={mdiCheck} size={0.8} />}
                                        Full Book Content
                                    </div>
                                    
                                    <div 
                                        className={`sidebar-context-menu-item ${selectedContext.bookSummary ? 'selected' : ''}`}
                                        onClick={() => toggleContextOption('bookSummary')}
                                    >
                                        {selectedContext.bookSummary && <Icon path={mdiCheck} size={0.8} />}
                                        Book Summary
                                    </div>
                                    
                                    {/* Add World Info option */}
                                    <div 
                                        className={`sidebar-context-menu-item ${selectedContext.worldInfo ? 'selected' : ''}`}
                                        onClick={() => toggleContextOption('worldInfo')}
                                    >
                                        {selectedContext.worldInfo && <Icon path={mdiCheck} size={0.8} />}
                                        World Info
                                    </div>
                                    
                                    <div 
                                        className="sidebar-context-menu-item with-submenu"
                                        onMouseEnter={(e) => {
                                            setHoveredItem('fullChapter');
                                            checkMenuPosition('fullChapter', e);
                                        }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Full Chapter Content
                                        <Icon path={mdiChevronUp} size={0.8} />
                                        
                                        {hoveredItem === 'fullChapter' && (
                                            <div className={`sidebar-submenu ${menuPositions.fullChapter ? 'grow-up' : ''}`}>
                                                {!state.currentNovel?.chapters ? (
                                                    <div className="sidebar-submenu-item loading">
                                                        <Icon path={mdiLoading} size={0.8} spin /> Loading chapters...
                                                    </div>
                                                ) : state.currentNovel.chapters.length === 0 ? (
                                                    <div className="sidebar-submenu-item empty-state">
                                                        No chapters available
                                                    </div>
                                                ) : state.currentNovel.chapters.map(chapter => (
                                                    <div 
                                                        key={chapter.id}
                                                        className={`sidebar-submenu-item ${selectedContext.chapters[chapter.id] ? 'selected' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleContextOption('chapter', chapter.id);
                                                        }}
                                                    >
                                                        {selectedContext.chapters[chapter.id] && <Icon path={mdiCheck} size={0.8} />}
                                                        {chapter.title}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div 
                                        className="sidebar-context-menu-item with-submenu"
                                        onMouseEnter={(e) => {
                                            setHoveredItem('chapterSummary');
                                            checkMenuPosition('chapterSummary', e);
                                        }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Chapter Summary
                                        <Icon path={mdiChevronUp} size={0.8} />
                                        
                                        {hoveredItem === 'chapterSummary' && (
                                            <div className={`sidebar-submenu ${menuPositions.chapterSummary ? 'grow-up' : ''}`}>
                                                {!state.currentNovel?.chapters ? (
                                                    <div className="sidebar-submenu-item loading">
                                                        <Icon path={mdiLoading} size={0.8} spin /> Loading chapters...
                                                    </div>
                                                ) : state.currentNovel.chapters.length === 0 ? (
                                                    <div className="sidebar-submenu-item empty-state">
                                                        No chapters available
                                                    </div>
                                                ) : state.currentNovel.chapters.map(chapter => (
                                                    <div 
                                                        key={chapter.id}
                                                        className={`sidebar-submenu-item ${selectedContext.chapterSummaries[chapter.id] ? 'selected' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleContextOption('chapterSummary', chapter.id);
                                                        }}
                                                    >
                                                        {selectedContext.chapterSummaries[chapter.id] && <Icon path={mdiCheck} size={0.8} />}
                                                        {chapter.title}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div 
                                        className="sidebar-context-menu-item with-submenu"
                                        onMouseEnter={(e) => {
                                            setHoveredItem('scenes');
                                            checkMenuPosition('scenes', e);
                                        }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Scene Content
                                        <Icon path={mdiChevronUp} size={0.8} />
                                        
                                        {hoveredItem === 'scenes' && (
                                            <div className={`sidebar-submenu ${menuPositions.scenes ? 'grow-up' : ''}`}>
                                                {!state.currentNovel?.chapters ? (
                                                    <div className="sidebar-submenu-item loading">
                                                        <Icon path={mdiLoading} size={0.8} spin /> Loading chapters...
                                                    </div>
                                                ) : state.currentNovel.chapters.length === 0 ? (
                                                    <div className="sidebar-submenu-item empty-state">
                                                        No chapters available
                                                    </div>
                                                ) : state.currentNovel.chapters.map(chapter => (
                                                    <div 
                                                        key={chapter.id}
                                                        className="sidebar-submenu-item with-submenu"
                                                        onMouseEnter={(e) => {
                                                            handleChapterHover(chapter.id, e);
                                                        }}
                                                        onMouseLeave={() => setHoveredChapter(null)}
                                                    >
                                                        {chapter.title}
                                                        <Icon path={mdiChevronUp} size={0.8} />
                                                        
                                                        {hoveredChapter === chapter.id && (
                                                            <div className={`sidebar-subsubmenu ${menuPositions.chapters[chapter.id] ? 'grow-up' : ''}`}>
                                                                {!chapter.scenes || chapter.scenes.length === 0 ? (
                                                                    <div className="sidebar-submenu-item empty-state">
                                                                        No scenes available
                                                                    </div>
                                                                ) : chapter.scenes.map(scene => (
                                                                    <div 
                                                                        key={scene.id}
                                                                        className={`sidebar-submenu-item ${selectedContext.scenes[scene.id] ? 'selected' : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleContextOption('scene', scene.id);
                                                                        }}
                                                                    >
                                                                        {selectedContext.scenes[scene.id] && <Icon path={mdiCheck} size={0.8} />}
                                                                        {scene.summary ? `Scene ${scene.scene_number}: ${scene.summary.substring(0, 30)}...` : 
                                                                        `Scene ${scene.scene_number}`}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="sidebar-selected-context">
                            <span>Context:</span> {getSelectedContextDisplay()}
                        </div>
                    </Flex>
                </div>}
            </div>

            {/* Delete Confirmation Modal */}
            <Dialog.Root open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <Dialog.Content style={{ maxWidth: 450 }}>
                    <Dialog.Title>Confirm Delete</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        Delete this message and all that follow? This cannot be undone.
                    </Dialog.Description>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button variant="solid" color="red" onClick={confirmDelete}>
                            Delete
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
            
            {/* Error Modal */}
            <Dialog.Root open={errorModalOpen} onOpenChange={setErrorModalOpen}>
                <Dialog.Content style={{ maxWidth: 450 }}>
                    <Dialog.Title>
                        <Flex align="center" gap="2">
                            <Icon path={mdiAlert} size={1} color="#F03E3E" />
                            <Text>Error</Text>
                        </Flex>
                    </Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        {errorMessage}
                    </Dialog.Description>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft">
                                OK
                            </Button>
                        </Dialog.Close>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    );
};

export default SidebarChatContent;